<template>
  <div
    class="bottom-sheet-root"
    :data-elevated-layer="elevated ? '' : undefined"
  >
    <Transition name="bottom-sheet-backdrop">
      <div
        v-if="open && backdrop"
        sheet-backdrop
        :class="[
          'fixed inset-0 bg-gray-900/50 dark:bg-gray-900/80',
          band.backdrop
        ]"
        @click="close"
      ></div>
    </Transition>
    <Transition name="bottom-sheet-slide" @after-leave="emit('hide-sheet')">
      <div
        v-if="open"
        role="dialog"
        aria-modal="true"
        :class="panelClasses"
        :style="panelStyle"
        @keydown.escape="onPanelEscape"
        @touchstart="onTouchStart"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd"
      >
        <div class="p-4 rounded-t-lg bg-white dark:bg-gray-800 z-20">
          <div class="flex items-center gap-3 min-h-8">
            <button
              type="button"
              class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-transparent text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:hover:bg-gray-600 dark:hover:text-white cursor-pointer"
              :class="closePosition === 'end' ? 'order-last ms-auto' : ''"
              @click="close"
            >
              <CloseIcon class="w-4" />
              <span class="sr-only">Close</span>
            </button>
            <slot name="title">
              <h5 v-if="title" class="text-base font-semibold text-gray-900">
                {{ title }}
              </h5>
            </slot>
            <slot name="action"></slot>
          </div>
          <slot name="header"></slot>
        </div>
        <div
          class="overflow-y-auto min-h-0"
          :class="{ 'pb-[env(safe-area-inset-bottom)]': !$slots.footer }"
        >
          <slot></slot>
        </div>
        <!-- The grid's implicit third row (auto), so neither row template changes. -->
        <div
          v-if="$slots.footer"
          sheet-footer
          class="px-4 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] border-t border-gray-100 bg-white dark:border-gray-700 dark:bg-gray-800"
        >
          <slot name="footer"></slot>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { visualViewportRect } from "@/lib/anchoredPosition";

import { useSwipeDismiss } from "@/composables/useSwipeDismiss";
import CloseIcon from "@/images/x.svg?component";

// A modal sheet that slides up and fills the screen below a small gap. The
// header holds the close icon, the title, an optional action on the right and
// whatever the `header` slot adds, such as a search field; the body scrolls. Nothing is
// sized from the soft keyboard on purpose: the keyboard simply covers the lower
// part of the body, and everything a user must reach while typing stays in the
// header. An optional `footer` slot sits under the body and takes over the
// home-indicator inset; the keyboard covers it too, so keep it for an action
// that can wait for the keyboard to close.
// Mounts closed and slides up; closes from the close icon, the backdrop, Escape,
// a downward swipe or `close()`, and emits `hide-sheet` once the leave transition
// has played so the parent can unmount.

const emit = defineEmits<{
  (e: "show-sheet"): void;
  (e: "hide-sheet"): void;
}>();

const props = withDefaults(
  defineProps<{
    title?: string;
    backdrop?: boolean;
    // Lifts the sheet above every dialog band (Seranova modals at 1020/1030,
    // Bootstrap modals at 1050, tippy popovers at 9999) for a sheet opened
    // from inside one, and marks the root as an elevated layer so popovers
    // beneath stay open while the user presses inside it
    // (lib/popoverOutsidePress).
    elevated?: boolean;
    // Opt-in content-height variant: the panel sizes to its content and
    // anchors to the bottom instead of stretching from the top gap to the
    // bottom, so the dimmed page stays visible above a short sheet. Default
    // false keeps every existing consumer (SelectInputSheet.vue) byte-for-byte
    // unchanged.
    fit?: boolean;
    // Which edge of the title row holds the close icon. Default "start" keeps
    // the existing leading-edge layout; "end" moves it to the trailing edge for
    // sheets whose design puts the title first.
    closePosition?: "start" | "end";
  }>(),
  {
    title: "",
    backdrop: true,
    elevated: false,
    fit: false,
    closePosition: "start"
  }
);

// 1002/1003 sits above the mobile nav drawer (z-[1001], fixed only below `md`)
// and below spinners, modals, flyouts and datepickers. 10000/10001 clears the
// tippy popovers at 9999 so their host is dimmed like everything else.
// Placeholders for TB-2193's band map.
const BANDS = {
  sheet: { panel: "z-[1003]", backdrop: "z-[1002]" },
  elevated: { panel: "z-[10001]", backdrop: "z-[10000]" }
};

const band = computed(() => (props.elevated ? BANDS.elevated : BANDS.sheet));

// Complete literals for Tailwind's scanner (two full strings, not one built
// from fragments) because Tailwind runs with `important: true`, so an inline
// `top`/`max-height` would lose to the utility.
const PANEL =
  "fixed left-0 right-0 bottom-0 top-[calc(env(safe-area-inset-top)+1.5rem+var(--sheet-pan,0px))] w-full rounded-t-lg overflow-x-hidden bg-white dark:bg-gray-800 grid grid-rows-[auto_1fr] bottom-sheet";

// The `fit` variant drops the top pin (so height comes from content, not the
// full available gap) and caps growth with a max-height built from the same
// inputs, so a short sheet still leaves the dimmed page visible above it.
// `grid-rows-[auto_minmax(0,1fr)]` (not `auto_1fr`) is required here: without
// the `minmax(0, ...)` floor, an auto-height grid's second row sizes to its
// content's min-content height rather than shrinking to fit the cap, and the
// body would overflow the panel instead of scrolling internally.
const FIT_PANEL =
  "fixed left-0 right-0 bottom-0 top-auto max-h-[calc(100dvh-env(safe-area-inset-top)-1.5rem-var(--sheet-pan,0px))] w-full rounded-t-lg overflow-x-hidden bg-white dark:bg-gray-800 grid grid-rows-[auto_minmax(0,1fr)] bottom-sheet";

const panelClasses = computed(() => [
  props.fit ? FIT_PANEL : PANEL,
  band.value.panel
]);

// The sheet's top follows the visible area's top, not the layout's. iOS
// Safari pans the visible area down over the layout when the keyboard opens
// for a field inside a fixed element, and a header pinned to the layout's top
// would scroll off screen with it; browsers that never pan report 0. Only the
// offset is used: height stays the layout's, and the keyboard covers the
// lower rows. The gap leaves a strip of the dimmed page visible below the
// status bar (and tappable to close).
const visibleTop = ref(0);

const measureVisibleTop = () => {
  visibleTop.value = visualViewportRect().top;
};

let frame: number | null = null;
const scheduleMeasure = () => {
  if (frame !== null) return;
  frame = requestAnimationFrame(() => {
    frame = null;
    measureVisibleTop();
  });
};

// Both style sources merge rather than replace: the pan offset positions the
// panel's top and must survive a drag, which only adds a transform.
const panelStyle = computed(() => ({
  "--sheet-pan": `${Math.round(visibleTop.value)}px`,
  ...dragStyle.value
}));

// The panel is a v-if child of <Transition>, so Vue owns its mount and
// unmount: closing flips `open`, the leave transition plays, @after-leave
// emits hide-sheet for the parent to unmount.
const open = ref(false);

const close = () => {
  open.value = false;
};

// Fourth dismissal path, alongside the close button, the backdrop and Escape.
// Declared after `close` because a `<script setup>` call above it would read the
// binding in its temporal dead zone. Routed through `close` so a swipe plays the
// same leave transition, and so `hide-sheet` still fires for the parent.
const { dragStyle, onTouchStart, onTouchMove, onTouchEnd } =
  useSwipeDismiss(close);

// A sheet can open on top of a drawer or a modal that also closes on a
// document-level Escape. With focus inside the sheet the key reaches the panel
// first; closing here and stopping the event keeps the host open. The
// document listener stays as the fallback for a sheet focus never entered.
const onDocumentKeydown = (event: KeyboardEvent) => {
  if (event.key === "Escape") close();
};

const onPanelEscape = (event: KeyboardEvent) => {
  event.stopPropagation();
  close();
};

defineExpose({ close });

onMounted(() => {
  document.addEventListener("keydown", onDocumentKeydown);
  measureVisibleTop();
  window.visualViewport?.addEventListener("resize", scheduleMeasure);
  window.visualViewport?.addEventListener("scroll", scheduleMeasure, {
    passive: true
  });
  // Mount closed, then flip open so the slide-in plays from off screen.
  open.value = true;
  emit("show-sheet");
});

onBeforeUnmount(() => {
  document.removeEventListener("keydown", onDocumentKeydown);
  window.visualViewport?.removeEventListener("resize", scheduleMeasure);
  window.visualViewport?.removeEventListener("scroll", scheduleMeasure);
  if (frame !== null) cancelAnimationFrame(frame);
});
</script>

<style lang="scss">
.bottom-sheet-slide-enter-active,
.bottom-sheet-slide-leave-active {
  transition: transform 0.3s ease-in-out;
}
.bottom-sheet-slide-enter-from,
.bottom-sheet-slide-leave-to {
  transform: translateY(100%);
}
.bottom-sheet-backdrop-enter-active,
.bottom-sheet-backdrop-leave-active {
  transition: opacity 0.3s ease;
}
.bottom-sheet-backdrop-enter-from,
.bottom-sheet-backdrop-leave-to {
  opacity: 0;
}
</style>
